<template>
  <div class="side-modal-overlay" @click="$emit('close')">
    <div class="side-modal" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <div class="title-group">
            <h2 class="modal-title">Notifications</h2>
          </div>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <div class="notification-panel">
          <!-- Header Actions -->
          <div class="notification-panel__header">
            <div class="notification-panel__tabs">
              <button 
                class="notification-tab" 
                :class="{ 'notification-tab--active': activeTab === 'all' }"
                @click="activeTab = 'all'"
              >
                All
              </button>
              <button 
                class="notification-tab"
                :class="{ 'notification-tab--active': activeTab === 'unread' }"
                @click="activeTab = 'unread'"
              >
                Unread
                <span v-if="unreadCount > 0" class="notification-tab__badge">{{ unreadCount }}</span>
              </button>
            </div>
            <button 
              class="mark-all-btn" 
              :class="{ 'mark-all-btn--loading': markingAllRead }"
              :disabled="markingAllRead || unreadCount === 0"
              @click="markAllAsRead"
            >
              <div v-if="markingAllRead" class="mark-all-btn__loader"></div>
              <span v-else>Mark all as read</span>
            </button>
          </div>

          <!-- Notification List -->
          <div ref="notificationScroll" class="notification-panel__content">
            <!-- Loading State -->
            <div v-if="loading" class="notification-panel__loading">
              <div class="small-loader-container">
                <div class="small-loader">
                  <div class="loader-ring"></div>
                  <div class="loader-ring loader-ring-inner"></div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredNotifications.length === 0" class="notification-empty">
              <div class="notification-empty__icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" stroke="#CBD5E1" stroke-width="1.5" fill="none"/>
                </svg>
              </div>
              <h3 class="notification-empty__title">No notifications yet</h3>
              <p class="notification-empty__text">When you get notifications, they will show up here</p>
            </div>

            <!-- Notifications Content -->
            <template v-else>
              <!-- Date Groups -->
              <div
                v-for="(group, dateKey) in groupedNotifications"
                :key="'group-' + dateKey"
                class="notification-group"
              >
                <div class="notification-group__date">
                  <span>{{ formatDateLabel(dateKey) }}</span>
                </div>

                <!-- Notification Items -->
                <div
                  v-for="(notification, idx) in group"
                  :key="'notif-' + idx"
                  class="notification-item"
                  :class="{ 'notification-item--unread': !notification.read }"
                  @click="handleNotificationClick(notification)"
                >
                  <div v-if="!notification.read" class="notification-item__indicator"></div>
                  
                  <!-- Icon -->
                  <div class="notification-item__icon">
                    <img
                      v-if="notification.raw?.owner_image || notification.raw?.token_image"
                      :src="notification.raw?.owner_image || notification.raw?.token_image"
                      :alt="notification.type"
                    />
                    <div v-else class="notification-item__icon-default">
                      <svg v-if="notification.type === 'Buy' || notification.type === 'Sell'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M3.89701 17.86L7.80701 13.95L10.636 16.778L15.207 12.208L17 14V9H12L13.793 10.793L10.636 13.95L7.80801 11.121L2.86201 16.067C2.29194 14.787 1.99821 13.4012 2.00001 12C2.00001 6.477 6.47701 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C10.418 22.001 8.85839 21.6262 7.44962 20.9065C6.04084 20.1867 4.82321 19.1425 3.89701 17.86Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="notification.type === 'Invitation' || notification.type === 'Esop Invitation'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="notification.type === 'Gift'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6H17.82C17.93 5.69 18 5.35 18 5C18 3.34 16.66 2 15 2C13.95 2 13.04 2.54 12.5 3.35L12 4.02L11.5 3.34C10.96 2.54 10.05 2 9 2C7.34 2 6 3.34 6 5C6 5.35 6.07 5.69 6.18 6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM15 4C15.55 4 16 4.45 16 5C16 5.55 15.55 6 15 6C14.45 6 14 5.55 14 5C14 4.45 14.45 4 15 4ZM9 4C9.55 4 10 4.45 10 5C10 5.55 9.55 6 9 6C8.45 6 8 5.55 8 5C8 4.45 8.45 4 9 4ZM20 19H4V17H20V19ZM20 14H4V8H9.08L7 10.83L8.62 12L11 8.76L12 7.4L13 8.76L15.38 12L17 10.83L14.92 8H20V14Z" fill="currentColor"/>
                      </svg>
                      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="currentColor"/>
                      </svg>
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="notification-item__content">
                    <div class="notification-item__header">
                      <span class="notification-item__type">{{ removeUnderscore(notification.type) }}</span>
                      <span class="notification-item__time">{{ formatISOTime(notification.createdAt) }}</span>
                    </div>
                    <p class="notification-item__message">{{ notification.message }}</p>

                    <!-- Transaction Details (Buy/Sell) -->
                    <div
                      v-if="notification.type === 'Sell' || notification.type === 'Buy'"
                      class="notification-item__details"
                    >
                      <div class="notification-item__detail">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        <span>{{ notification.raw.amount.toLocaleString() }} {{ notification.raw.token_symbol }}</span>
                      </div>
                      <div class="notification-item__amount">
                        {{ formatAmountToDollar(notification.raw.price * notification.raw.amount, 2, notification.raw.currency || "USD") }}
                      </div>
                    </div>

                    <!-- Invitation Actions -->
                    <div
                      v-if="(notification.type === 'Esop Invitation' || notification.type === 'Invitation') && (!notification.raw.status || notification.raw.status === 'Pending')"
                      class="notification-item__actions"
                    >
                      <button class="notification-action notification-action--primary">Review Invitation</button>
                    </div>

                    <!-- Status Badge -->
                    <span
                      v-if="notification.raw?.status && notification.raw.status !== 'Pending'"
                      class="notification-item__status"
                      :class="{
                        'notification-item__status--accepted': notification.raw.status === 'Accepted',
                        'notification-item__status--terminated': notification.raw.status === 'Terminated' || notification.raw.status === 'Rejected'
                      }"
                    >
                      {{ notification.raw.status }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Load More -->
              <div ref="notificationBottom">
                <div v-if="loadingMore" class="notification-loader">
                  <div class="small-loader-container">
                    <div class="small-loader">
                      <div class="loader-ring"></div>
                      <div class="loader-ring loader-ring-inner"></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div v-if="!loadingMore" class="notification-panel__footer">
                <p class="notification-end">You're all caught up!</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmountToDollar, formatISOTime, removeUnderscore } from "@/utils/helpers.js";

export default {
  name: "NotificationModal",

  emits: ["close", "notification-click"],

  props: {
    notifications: {
      type: Object,
      required: true,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    unreadCount: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      activeTab: "all",
      markingAllRead: false
    };
  },

  computed: {
    filteredNotifications() {
      const allNotifications = Object.values(this.notifications).flat();
      if (this.activeTab === "unread") {
        return allNotifications.filter(n => !n.read);
      }
      return allNotifications;
    },

    groupedNotifications() {
      if (this.activeTab === "all") {
        return this.notifications;
      }
      
      // For unread tab, regroup by date
      const filtered = this.filteredNotifications;
      const grouped = {};
      
      filtered.forEach(notification => {
        const date = notification.createdAt.split("T")[0];
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(notification);
      });
      
      return grouped;
    }
  },

  mounted() {
    document.body.style.overflow = "hidden";
    this.setupObserver();
  },

  beforeUnmount() {
    document.body.style.overflow = "";
  },

  methods: {
    formatAmountToDollar,
    formatISOTime,
    removeUnderscore,

    formatDateLabel(dateKey) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const dateObj = new Date(dateKey);
      const todayStr = today.toISOString().split("T")[0];
      const yesterdayStr = yesterday.toISOString().split("T")[0];
      
      if (dateKey === todayStr) {
        return "TODAY";
      } else if (dateKey === yesterdayStr) {
        return "YESTERDAY";
      } else {
        return dateObj.toLocaleDateString("en-US", { 
          month: "short", 
          day: "numeric",
          year: dateObj.getFullYear() !== today.getFullYear() ? "numeric" : undefined
        }).toUpperCase();
      }
    },

    setupObserver() {
      this.$nextTick(() => {
        if (this.$refs.notificationBottom) {
          const options = {
            root: this.$refs.notificationScroll,
            rootMargin: "0px",
            threshold: 0.5
          };
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.$emit("load-more");
              }
            });
          }, options);
          observer.observe(this.$refs.notificationBottom);
        }
      });
    },

    handleNotificationClick(notification) {
      this.$emit("notification-click", notification);
    },

    markAllAsRead() {
      this.markingAllRead = true;
      this.$emit("mark-all-read");
      // Reset state after animation
      setTimeout(() => {
        this.markingAllRead = false;
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
/* ========================================
   SIDE MODAL (WRAPPER)
   ======================================== */
.side-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(4px);
  z-index: 50;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.side-modal {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: #FFFFFF;
  box-shadow: -8px 0 32px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;
}

@media (max-width: 480px) {
  .side-modal { max-width: 100%; }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E2E8F0;
  background: #FFFFFF;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.modal-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #64748B;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #FEF2F2;
  color: #EF4444;
}

.modal-content {
  flex: 1;
  overflow: hidden;
  background: #FFFFFF;
}

/* ========================================
   NOTIFICATION PANEL
   ======================================== */
.notification-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.notification-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.notification-panel__tabs {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
}

.notification-panel__content {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
}

.notification-panel__content::-webkit-scrollbar {
  width: 6px;
}

.notification-panel__content::-webkit-scrollbar-track {
  background: transparent;
}

.notification-panel__content::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 3px;
}

.notification-panel__content::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

.notification-panel__footer {
  padding: 16px 0;
}

.notification-panel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

/* ========================================
   TABS
   ======================================== */
.notification-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification-tab:hover {
  color: #475569;
}

.notification-tab--active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.notification-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  font-weight: 500;
  border-radius: 9px;
}

/* ========================================
   MARK ALL BUTTON
   ======================================== */
.mark-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: #4d84ff;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
}

.mark-all-btn:hover:not(:disabled) {
  background: rgba(77, 132, 255, 0.1);
}

.mark-all-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.mark-all-btn--loading {
  color: #94a3b8;
}

.mark-all-btn__loader {
  width: 14px;
  height: 14px;
  border: 2px solid #e2e8f0;
  border-top-color: #4d84ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========================================
   NOTIFICATION GROUPS
   ======================================== */
.notification-group__date {
  display: flex;
  align-items: center;
  padding: 20px 0 12px;
}

.notification-group__date span {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ========================================
   NOTIFICATION ITEMS
   ======================================== */
.notification-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification-item:hover {
  border-color: #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.notification-item--unread {
  background: linear-gradient(135deg, rgba(77, 132, 255, 0.02) 0%, rgba(77, 132, 255, 0.05) 100%);
  border-color: rgba(77, 132, 255, 0.2);
}

.notification-item--unread:hover {
  border-color: rgba(77, 132, 255, 0.3);
}

.notification-item__indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 24px;
  background: #4d84ff;
  border-radius: 0 3px 3px 0;
}

.notification-item__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-item__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.notification-item__icon-default {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  color: #4d84ff;
}

.notification-item__content {
  flex: 1;
  min-width: 0;
}

.notification-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.notification-item__type {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}

.notification-item__time {
  font-size: 11px;
  color: #94a3b8;
}

.notification-item__message {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

.notification-item__details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.notification-item__detail {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.notification-item__detail svg {
  color: #94a3b8;
}

.notification-item__amount {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}

.notification-item__actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.notification-item__status {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
}

.notification-item__status--accepted {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.notification-item__status--terminated {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* ========================================
   ACTION BUTTONS
   ======================================== */
.notification-action {
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification-action--primary {
  background: #4d84ff;
  color: #ffffff;
}

.notification-action--primary:hover {
  background: #3b6fe8;
}

.notification-action--secondary {
  background: #f1f5f9;
  color: #475569;
}

.notification-action--secondary:hover {
  background: #e2e8f0;
}

/* ========================================
   FOOTER & EMPTY STATES
   ======================================== */
.notification-end {
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  padding: 8px 0;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.notification-empty__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f8fafc;
  border-radius: 50%;
  margin-bottom: 20px;
}

.notification-empty__title {
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.notification-empty__text {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
  max-width: 240px;
}

/* ========================================
   LOADER
   ======================================== */
.small-loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
}

.small-loader {
  position: relative;
  width: 48px;
  height: 48px;
}

.loader-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid #E2E8F0;
  border-top-color: #4D84FF;
  animation: spin 0.9s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.loader-ring-inner {
  width: 65%;
  height: 65%;
  top: 17.5%;
  left: 17.5%;
  border-width: 2px;
  border-color: #F1F5F9;
  border-right-color: #4D84FF;
  opacity: 0.8;
  animation: spinReverse 0.7s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

@keyframes spinReverse {
  0% { transform: rotate(360deg); }
  100% { transform: rotate(0deg); }
}

.notification-loader {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}
</style>
